import type { AddressValue, AiProcessedResponse, FormField, Question, ResponseValue } from '../types';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\d{3}-\d{3}-\d{4}$/;
const REQUIRED_ADDRESS_PARTS: Array<Exclude<keyof AddressValue, 'country'>> = ['addressLine1', 'city', 'stateProvince', 'postalCode'];

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isAiProcessedResponse(response: ResponseValue | undefined): response is AiProcessedResponse {
  return Boolean(response)
    && typeof response === 'object'
    && !Array.isArray(response)
    && 'outcome' in response;
}

function isAddressValue(value: unknown): value is AddressValue {
  return isRecord(value)
    && 'addressLine1' in value
    && 'addressLine2' in value
    && 'city' in value
    && 'stateProvince' in value
    && 'postalCode' in value;
}

function isCompleteAddress(value: unknown): value is AddressValue {
  if (!isAddressValue(value)) return false;
  return REQUIRED_ADDRESS_PARTS.every((part) => hasText(value[part]));
}

function isRequiredFormFieldAnswered(field: FormField, value: unknown): boolean {
  if (field.type === 'address') return isCompleteAddress(value);
  if (!hasText(value)) return false;
  if (field.type === 'email') return EMAIL_REGEX.test(value.trim());
  if (field.type === 'phone') return PHONE_REGEX.test(value.trim());
  if (field.validation?.minLength !== undefined && value.length < field.validation.minLength) return false;
  if (field.validation?.maxLength !== undefined && value.length > field.validation.maxLength) return false;
  return true;
}

function isMailtoResponseComplete(response: ResponseValue | undefined): boolean {
  if (!isRecord(response)) return false;
  const company = response.company;
  const email = response.email;
  return hasText(company) && hasText(email) && EMAIL_REGEX.test(email.trim());
}

export function isQuestionAnswered(question: Question, response: ResponseValue | undefined): boolean {
  if (question.type === 'Information') return true;
  if (question.type === 'AiProcessed') return isAiProcessedResponse(response) && response.outcome !== undefined;
  if (question.type === 'Mailto') return isMailtoResponseComplete(response);
  if (response === undefined) return false;

  if (question.type === 'FormEntry') {
    if (!isRecord(response)) return false;
    const requiredFields = (question.formFields ?? []).filter((field) => field.isRequired);
    return requiredFields.every((field) => isRequiredFormFieldAnswered(field, response[`field_${field.id}`]));
  }

  return true;
}
